import React, {createContext, useContext, useState} from 'react';
import jwtAxios from '../../@crema/services/auth/jwt-auth/jwt-api';
import {useAuthUser} from '../../@crema/utility/AuthHooks';
import {Quote} from '../quotes/QuotesProvider';

type QuoteContextType = {
  quote: Quote | null;
  loading: boolean;
};

interface QuoteActionsContextType {
  create: Function;
  remove: Function;
  patch: Function;
  fetchQuote: Function;
  createQuoteField: Function;
  deleteQuoteField: Function;
}

const defaultQuoteContext = {
  quote: null,
  loading: false,
};

const QuoteContext = createContext<QuoteContextType>(defaultQuoteContext);

const QuoteActionsContext = createContext<QuoteActionsContextType>({
  create: () => {},
  remove: () => {},
  patch: () => {},
  fetchQuote: () => {},
  createQuoteField: () => {},
  deleteQuoteField: () => {},
});

export const useQuoteContext = () => useContext(QuoteContext);
export const useQuoteActionsContext = () => useContext(QuoteActionsContext);

const QuoteProvider = ({children}: any) => {
  const {user} = useAuthUser();
  const [quote, setQuote] = useState<Quote | null>(defaultQuoteContext.quote);
  const [loading, setLoading] = useState(defaultQuoteContext.loading);

  const fetchQuote = (quoteId: string) => {
    setLoading(true);
    getQuote(quoteId)
      .then(({data}) => {
        setQuote(data.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const create = (column: string, label: string, type: number) => {
    setLoading(true);
    createQuote(column, label, type).then(() => {
      setLoading(false);
    });
  };

  const remove = (id: string) => {
    setLoading(true);
    deleteQuote(id).then(() => {
      setLoading(false);
    });
  };

  const patch = (quoteId: string, column: string, label: string) => {
    return jwtAxios.patch(`/quote/update`, {
      id: quoteId,
      column,
      label,
    });
  };

  const getQuote = async (quoteId: string) => {
    return jwtAxios.get(`/quote/get/${quoteId}`);
  };

  const createQuote = async (column: string, label: string, type: number) => {
    return jwtAxios.post(`/quote/create/${user.id}`, {
      column,
      label,
      type,
    });
  };

  const deleteQuote = async (id: string) => {
    return jwtAxios.delete(`/quote/remove/${id}`);
  };

  const createQuoteField = async (key: string) => {
    return jwtAxios.post(`/quote-field/create/${quote?.id}`, {
      key,
    });
  };

  const deleteQuoteField = async (id: string) => {
    return jwtAxios.delete(`/quote-field/remove/${id}`);
  };

  return (
    <QuoteContext.Provider value={{quote, loading}}>
      <QuoteActionsContext.Provider
        value={{
          create,
          remove,
          patch,
          fetchQuote,
          createQuoteField,
          deleteQuoteField,
        }}>
        {children}
      </QuoteActionsContext.Provider>
    </QuoteContext.Provider>
  );
};

export default QuoteProvider;
